import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useGetScoreSheetSummary } from "service/comment.service";
import { useDpatRealtimeQuerySync } from "hooks/useDpatRealtimeQuerySync";

const DpatCommentFeedbackContext = createContext({
  hideCommentAuthorNames: false,
  assessmentWorkflowStatus: null,
  scoreSheetSummary: null,
  assessmentStartTables: null,
  registerIndicatorMaxScore: () => {},
  getIndicatorMaxScore: () => null,
});

export function DpatCommentFeedbackProvider({
  assessmentStatus,
  districtId,
  year,
  assessmentStartTables,
  children,
}) {
  const { data: scoreSheetResponse } = useGetScoreSheetSummary(districtId, year, "DPAT");
  useDpatRealtimeQuerySync(districtId, year);
  const [maxScoreBySlug, setMaxScoreBySlug] = useState({});

  const registerIndicatorMaxScore = useCallback((slug, maxScore) => {
    const key = String(slug || "").trim().toLowerCase();
    const numeric = Number(maxScore);
    if (!key || !Number.isFinite(numeric)) return;
    setMaxScoreBySlug((prev) => {
      if (prev[key] === numeric) return prev;
      return { ...prev, [key]: numeric };
    });
  }, []);

  const getIndicatorMaxScore = useCallback(
    (slug) => {
      const key = String(slug || "").trim().toLowerCase();
      const value = maxScoreBySlug[key];
      return Number.isFinite(value) ? value : null;
    },
    [maxScoreBySlug],
  );

  const value = useMemo(() => {
    const status = assessmentStatus?.status != null ? String(assessmentStatus.status).trim() : null;
    const scoreSheetSummary = scoreSheetResponse?.data || scoreSheetResponse || null;
    return {
      hideCommentAuthorNames: status === "Completed" || status === "Closed",
      assessmentWorkflowStatus: status,
      scoreSheetSummary,
      assessmentStartTables: assessmentStartTables ?? null,
      registerIndicatorMaxScore,
      getIndicatorMaxScore,
    };
  }, [
    assessmentStatus?.status,
    scoreSheetResponse,
    assessmentStartTables,
    registerIndicatorMaxScore,
    getIndicatorMaxScore,
  ]);

  return (
    <DpatCommentFeedbackContext.Provider value={value}>
      {children}
    </DpatCommentFeedbackContext.Provider>
  );
}

export function useDpatCommentFeedback() {
  return useContext(DpatCommentFeedbackContext);
}

export default DpatCommentFeedbackContext;
